import { Injectable, NgZone } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Patient } from '../models/patient.model';
import moment from 'moment';
import { MatDialog } from '@angular/material/dialog';
import { NotificationsService } from './notifications.service';
import { LocaleService } from './locale.service';
import { DialogComponent } from '../shared/components/dialog/dialog.component';
import { DoctorService } from './doctor.service';
import { PrescriptionGeneratorService } from '../enums/prescription-generator-service.enum';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root'
})
export class MemedIntegrationService {
  private scriptLoaded = false;
  private readyResolver!: (value?: unknown) => void;
  private readyPromise = new Promise((resolve) => (this.readyResolver = resolve));

  private readonly STORAGE_KEY = 'memed_prescription_ids';
  prescriptionIds: string[] = [];

  constructor(
    private dialog: MatDialog,
    private notificationsService: NotificationsService,
    private localeService: LocaleService,
    private doctorService: DoctorService,
    private toastService: ToastService,
    private ngZone: NgZone
  ) {
    this.loadPrescriptionsFromStorage();
  }

  async initialize(patient: Patient): Promise<void> {
    try {
      const dialogRef = this.dialog.open(DialogComponent, {
        width: '400px',
        panelClass: 'bg-dark',
        disableClose: true,
        data: {
          title: this.localeService.getString('dialog_processing_title'),
          message: 'dialog_processing_memed_message'
        }
      });

      const memedToken = await this.doctorService.getPrescriptionGeneratorServiceToken(PrescriptionGeneratorService.MEMED);

      if (!this.scriptLoaded) {
        await this.loadScript(memedToken);
        this.scriptLoaded = true;
      }

      await this.readyPromise; // Wait until Memed is ready

      await this.setPatient(patient);
      await this.setCustomizations();
      await this.mdHub?.module?.show('plataforma.prescricao');

      dialogRef.close();
    } catch (error) {
      this.dialog.closeAll();
      const dialogData = {
        title: this.localeService.getString('dialog_title_warning'),
        message: 'memed_loading_error',
        canCancel: true
      };

      this.notificationsService.openErrorDialog(dialogData, true);
      throw null;
    }
  }

  private loadScript(memedToken: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (document.getElementById('memed-script')) {
        resolve();
        return;
      }

      const memedUrl = environment.production
        ? 'https://partners.memed.com.br/integration.js'
        : 'https://integrations.memed.com.br/modulos/plataforma.sinapse-prescricao/build/sinapse-prescricao.min.js';
      const script = document.createElement('script');
      script.id = 'memed-script';
      script.src = memedUrl;
      script.dataset.token = memedToken;
      script.dataset.color = '#d62a5f';

      script.addEventListener('load', () => {
        this.setupEventHandlers();
        resolve();
      });

      script.addEventListener('error', (e) => reject(e));
      document.body.appendChild(script);
    });
  }

  private setupEventHandlers(): void {
    console.log('Loading Memed script...');
    this.mdSinapsePrescricao?.event.add('core:moduleInit', async (module) => {
      if (module.name === 'plataforma.prescricao') {
        console.log('Memed script loaded!');
        this.readyResolver();

        this.mdHub?.event.add('prescricaoImpressa', async (prescriptionData) => {
          const prescriptionId = prescriptionData?.prescricao?.id;
          if (prescriptionId) this.addPrescriptionId(prescriptionId);

          await this.hideMemedModule(true);
        });
      }
    });
  }

  async hideMemedModule(success = true): Promise<void> {
    await this.mdHub?.module.hide('platform.sms-modal');
    await this.mdHub?.module.hide('plataforma.prescricao');

    if (success) {
      this.ngZone.run(() => {
        this.toastService.openSuccessToast();
      });
    }
  }

  async logoutFromMemed(): Promise<void> {
    await this.mdHub?.command.send('plataforma.sdk', 'logout');
    this.clearStorage();
  }

  getPrescriptionIds(): string[] {
    return [...this.prescriptionIds];
  }

  private async setPatient(patient: Patient): Promise<void> {
    await this.mdHub?.command?.send('plataforma.prescricao', 'setPaciente', {
      idExterno: patient.id,
      nome: patient.name,
      withoutCpf: true,
      data_nascimento: patient.birthDate || patient.birthDate === 0 ? moment.unix(patient.birthDate).utc().format('DD/MM/YYYY') : null,
      email: 'prescription@llamandoaldoctor.com'
    });
  }

  private async setCustomizations(): Promise<void> {
    await this.mdHub?.command?.send('plataforma.prescricao', 'setFeatureToggle', {
      optionsPrescription: false,
      allowShareModal: false,
      conclusionModalEdit: false,
      removePrescription: false
    });
  }

  private loadPrescriptionsFromStorage(): void {
    const raw = sessionStorage.getItem(this.STORAGE_KEY);
    this.prescriptionIds = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(this.prescriptionIds)) this.prescriptionIds = [];
  }

  private saveToStorage(): void {
    sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.prescriptionIds));
  }

  private clearStorage(): void {
    sessionStorage.removeItem(this.STORAGE_KEY);
    this.prescriptionIds = [];
  }

  private addPrescriptionId(id: string): void {
    if (!this.prescriptionIds.includes(id)) {
      this.prescriptionIds.push(id);
      this.saveToStorage();
    }
  }

  private get mdHub() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (window as any)?.MdHub;
  }

  private get mdSinapsePrescricao() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (window as any)?.MdSinapsePrescricao;
  }
}
